<template>
  <div class="lg:container lg:mx-auto">
    <Head title="Projects list" />
    <h1 class="mb-8 text-4xl font-bold w-1/3 md:w-1/6">My Projects</h1>
    <form class="" @submit.prevent="search">
      <div class="md:flex md:flex-wrap w-full justify-between gap-2 ">
        <text-input v-model="form.quote" name="quote" :error="form.errors.quote" class="flex-auto pb-4 max-w-md" label="Quote Number" tabindex="1" />
        <text-input v-model="form.company" name="company" :error="form.errors.company" class="flex-none w-1/3 pb-4 max-w-md" label="Company" tabindex="2" />
        <text-input v-model="form.phone" name="phone" :error="form.errors.phone" class="flex-none w-1/3 pb-4 max-w-md" type="tel" mask="(###) ###-####" label="Customer Phone Number" tabindex="3" />
      </div>
      <div class="md:flex md:flex-wrap w-full justify-between gap-2">
        <text-input v-model="form.title" name="title" :error="form.errors.title" class="flex-auto pb-4 max-w-md" label="Project Title" tabindex="4" />
        <text-input v-model="form.contact" name="contact" :error="form.errors.contact" class="flex-none w-1/3 pb-4 max-w-md" label="Customer Contact" tabindex="5" />
        <text-input v-model="form.email" name="email" :error="form.errors.email" class="flex-none w-1/3 pb-4 max-w-md" label="Customer Email" tabindex="6" />
      </div>
      <div class="md:flex md:flex-wrap w-full justify-between gap-2">
        <text-input v-model="form.city" name="city" :error="form.errors.city" class="flex-auto pb-4 max-w-md" label="City" tabindex="7" />
        <text-input v-model="form.owner" name="owner" :error="form.errors.owner" class="flex-none w-1/3 pb-4 max-w-md" label="Project Owner" tabindex="8" type="search" />
        <div class="flex-none w-1/3 pb-4 max-w-md">
          <date-range-picker ref="picker" v-model="dateRange" :ranges="ranges" :auto-apply="true" opens="left" @update="update">
            <template #input="picker">
              <div class="">
                <label class="form-label">Date Range</label>
                <span class="form-input">{{ getDate(picker.startDate) }} - {{ getDate(picker.endDate) }}</span>
              </div>
            </template>
            <!--    footer slot-->
            <template #footer="data">
              <div class="flex items-center gap-4 justify-end my-2 py-1 px-2 text-sm">
                <div>
                  {{ data.rangeText }}
                </div>
                <div class="flex items-stretch justify-around gap-2">
                  <button type="button" class="border border-blue-400 py-1 px-2" @click="data.clickCancel">Cancel</button>
                  <button v-if="! data.in_selection" type="button" class="btn-blue py-1 px-2" @click="data.clickApply">Apply</button>
                </div>
              </div>
            </template>
          </date-range-picker>
        </div>
      </div>
      <div class="md:flex md:flex-wrap w-full items-stretch justify-between gap-2 mb-4">
        <select-input v-model="form.status" :error="form.errors.status" class="flex-auto pb-4 max-w-md" label="Status">
          <option value="null" disabled selected>Choose...</option>
          <option v-for="(v,k) in statuses" :key="k" :value="k"> {{ v }}</option>
        </select-input>
        <div class="flex items-center justify-center md:justify-end gap-4">
          <button type="reset" class="h-10 px-8 border border-blue-400 text-blue-400 hover:bg-gray-400 hover:text-white focus:bg-gray-400 focus:text-white" tabindex="9" @click="reset()">Clear</button>
          <loading-button :loading="form.processing" class="btn-blue px-8 block h-10" type="submit" tabindex="10">Search</loading-button>
        </div>
      </div>
    </form>
    <!-- filters -->

    <hr class="border-t border-blue-400 mb-2" />

    <div class="bg-white overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left text-gray-600">
            <th width="5%">
              <a class="flex items-center justify-center pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'quote'}" role="button" @click.prevent="sortBy('quote')">
                <icon v-if="form.sort == 'quote' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Quote
              </a>
            </th>
            <th>
              <a class="flex items-center justify-start pl-4 pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'title'}" role="button" @click.prevent="sortBy('title')">
                <icon v-if="form.sort == 'title' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Project
              </a>
            </th>
            <th>
              <a class="flex items-center justify-start pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'city'}" role="button" @click.prevent="sortBy('city')">
                <icon v-if="form.sort == 'city' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                City
              </a>
            </th>
            <th>
              <a class="flex items-center justify-start pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'company'}" role="button" @click.prevent="sortBy('company')">
                <icon v-if="form.sort == 'company' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Company
              </a>
            </th>
            <th width="10%">
              <a class="flex items-center justify-start pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'total'}" role="button" @click.prevent="sortBy('total')">
                <icon v-if="form.sort == 'total' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Quote Value
              </a>
            </th>
            <th width="10%">
              <a class="flex items-center justify-center pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'status'}" role="button" @click.prevent="sortBy('status')">
                <icon v-if="form.sort == 'status' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Status
              </a>
            </th>
            <th width="10%">
              <a class="flex items-center justify-center pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'owner'}" role="button" @click.prevent="sortBy('owner')">
                <icon v-if="form.sort == 'owner' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Owner
              </a>
            </th>
            <th width="5%">
              <a class="flex items-center justify-end pb-4 pt-6 cursor-pointer" :class="{'text-blue-400' : form.sort == 'created_at'}" role="button" @click.prevent="sortBy('created_at')">
                <icon v-if="form.sort == 'created_at' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
                Created on
              </a>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(project, index) in projects.data" :key="project.id"
            :class="index % 2 == 0 ? 'bg-gray-100': 'bg-white'"
            class="text-black hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="border-t">
              <Link class="flex items-center justify-center px-4 py-4 font-bold focus:text-blue-500" :href="`/projects/${project.id}`">
                {{ project.quote }}
                <icon v-if="project.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="border-t">
              <div class="flex items-center">
                <Link class="flex items-center px-4 py-4 focus:text-blue-500" :href="`/projects/${project.id}`">
                  <div class="whitespace-normal">{{ project.title }}</div>
                  <icon v-if="project.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                </Link>
                <Link v-if="project.permission.update && ! project.deleted_at" :href="`/projects/${project.id}/edit`" class="p-3 cursor-pointer">
                  <icon name="edit" class="flex-shrink-0 w-4 h-4 fill-blue-400" />
                </Link>
              </div>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-4 py-4" :href="`/projects/${project.id}`" tabindex="-1">
                <div class="whitespace-normal text-left">{{ project.city }}</div>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-4 py-4" :href="`/projects/${project.id}`" tabindex="-1">
                <div class="whitespace-normal text-left">{{ project.company }}</div>
              </Link>
            </td>
            <td class="border-t text-right">
              <Link class="mr-2" :href="`/projects/${project.id}`" tabindex="-1">
                {{ project.total_str }}
              </Link>
            </td>
            <td class="border-t">
              <select-input v-if="project.permission.status" :model-value="project.status" class="p-0" @update:model-value="updateStatus(project, $event)">
                <option v-for="(v,k) in statusesList" :key="k" :value="k"> {{ v }}</option>
              </select-input>
            </td>
            <td class="w-px border-t">
              <Link class="flex items-center px-4" :href="`/projects/${project.id}`" tabindex="-1">
                <div class="whitespace-normal text-left">{{ project.owner }}</div>
              </Link>
            </td>
            <td class="w-px border-t text-right">
              <Link class="mr-2" :href="`/projects/${project.id}`" tabindex="-1">
                {{ project.created_on }}
              </Link>
            </td>
          </tr>
          <tr v-if="projects.data.length === 0">
            <td class="px-6 py-4 border-t text-center" colspan="8">No projects found.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- table -->

    <pagination class="mt-6" :links="projects.links" :total="projects.total" :query="query" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Pagination from '@/Shared/Pagination'
import mapValues from 'lodash/mapValues'
import DateRangePicker from 'daterange-picker-vue3'
import dayjs from'dayjs'

export default {
  components: {
    Head,
    Icon,
    Link,
    TextInput,
    SelectInput,
    Pagination,
    LoadingButton,
    DateRangePicker,
  },
  layout: Layout,
  props: {
    auth: Object,
    query: String,
    filters: Object,
    statuses: Object,
    projects: Object,
  },
  data() {
    return {
      dateRange: {
        startDate: this.filters.start ?? null,
        endDate  : this.filters.end ?? null,
      },
      form: this.$inertia.form({
        quote  : this.filters.quote,
        company: this.filters.company,
        phone  : this.filters.phone,
        title  : this.filters.title,
        contact: this.filters.contact,
        email  : this.filters.email,
        city   : this.filters.city,
        owner  : this.filters.owner,
        status : this.filters.status,
        sort   : this.filters.sort,
        dir    : this.filters.dir,
        start  : this.filters.start ?? null,
        end    : this.filters.end ?? null,
      }),
    }
  },
  computed: {
    sortIcon() {
      return this.form.dir == 'DESC' ? 'cheveron-down' : 'cheveron-up'
    },
    ranges () {
      let today = new Date()
      today.setHours(0, 0, 0, 0)

      let yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)

      let last7days = new Date
      last7days.setDate(today.getDate() - 6)
      last7days.setHours(0, 0, 0, 0)

      let thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      thisMonthStart.setHours(0, 0, 0, 0)

      let thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      thisMonthEnd.setHours(0, 0, 0, 0)

      return {
        'Today': [today, today],
        'Yesterday': [yesterday, yesterday],
        'Last 7 days' : [today, last7days],
        'This month': [thisMonthStart, thisMonthEnd],
        'Last month': [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
      }

    },
    statusesList () {
      const{ all, ...rest} = Object.assign({}, this.statuses)
      return rest
    },
  },
  methods: {
    update({startDate, endDate}) {
      this.form.start = startDate.toJSON().slice(0, 10)
      this.form.end  = endDate.toJSON().slice(0, 10)
    },
    search () {
      this.form.transform((data) => {
        return pickBy(data)
      }).get('/projects')
    },
    reset() {
      this.form.transform((data) => {
        data = mapValues(data, () => null)
        return pickBy(data)
      }).get('/projects')
    },
    updateStatus (project, status) {
      if (confirm('Are you sure you want to update the project status to ' + this.statuses[status]  +' ?')) {
        this.$inertia.put(`/projects/${project.id}/status/${status}`)
      }
    },
    sortBy(column) {
      this.form.sort = column
      this.toggleSortDir()
      this.search()
    },
    toggleSortDir() {
      this.form.dir = this.form.dir == 'DESC' ? 'ASC' : 'DESC'
    },
    getDate(date) {
      return date ?  dayjs(date).format('dddd, MMM DD') : ''
    },
  },
}
</script>
<style src="daterange-picker-vue3/dist/daterange-picker-vue3.css"></style>
<style>
.vue-daterange-picker {
  width: 100% !important;
}
.reportrange-text {
  border: none !important;
  padding: 0 !important;
}
</style>
